import React, { useMemo, useState } from 'react';
import Routes from 'routes';

import Overlay from 'shared/Overlay';
import { COLORS } from 'constants/colors';
import { customFetch } from 'utils/fetch';
import type Response from 'types/utils/Response';
import type IPendingNetsuiteIntegration from 'types/model/IPendingNetsuiteIntegration';

interface RetryResult {
  queued: boolean;
  message: string;
  validation_failed?: boolean;
  errors?: string[];
}

const PendingIntegrationsDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingIntegrations, setPendingIntegrations] = useState<
    IPendingNetsuiteIntegration[]
  >([]);
  const [resultByTransportId, setResultByTransportId] = useState<Record<number, string>>(
    {}
  );
  const [transportFilter, setTransportFilter] = useState('');
  const [validationAlert, setValidationAlert] = useState<{
    transportId: number;
    summary: string;
  } | null>(null);

  const fetchPendingIntegrations = () => {
    setLoading(true);

    customFetch<IPendingNetsuiteIntegration[]>(
      Routes.pending_netsuite_integrations_api_v1_decaf_transports_path(),
      undefined,
      'GET'
    )
      .then(setPendingIntegrations)
      .finally(() => setLoading(false));
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTransportFilter('');
    fetchPendingIntegrations();
  };

  const handleClose = () => {
    setIsOpen(false);
    setResultByTransportId({});
    setValidationAlert(null);
  };

  const handleRetry = (transportId: number) => () => {
    customFetch<Response<RetryResult>>(
      Routes.retry_netsuite_integration_api_v1_decaf_transport_path(transportId)
    ).then((response) => {
      if (response.status !== 'success') return;

      if (response.validation_failed) {
        const firstErrorLine = (response.errors?.[0] || '').split('\n')[0];

        setValidationAlert({
          transportId,
          summary: `${I18n.translate(
            'decaf.transports.pending_integrations.validation_failed_summary'
          )}: ${firstErrorLine}`,
        });
        return;
      }

      setResultByTransportId((previous) => ({
        ...previous,
        [transportId]: response.message,
      }));

      if (response.queued) {
        fetchPendingIntegrations();
      }
    });
  };

  const filteredIntegrations = useMemo(
    () =>
      pendingIntegrations.filter((integration) =>
        integration.local_identifier
          .toLowerCase()
          .includes(transportFilter.trim().toLowerCase())
      ),
    [pendingIntegrations, transportFilter]
  );

  return (
    <>
      <button
        type='button'
        className='button button--primary'
        data-cy='pending_netsuite_integrations_button'
        onClick={handleOpen}
      >
        {I18n.translate('decaf.transports.pending_integrations.button')}
      </button>

      {isOpen && (
        <Overlay
          title={I18n.translate('decaf.transports.pending_integrations.title')}
          secondaryText={I18n.translate('buttons.close')}
          onSecondaryClick={handleClose}
          disablePrevNextButtons={true}
          autoWidth={true}
        >
          {validationAlert && (
            <div
              className='alert-message alert-error'
              data-cy='netsuite_integration_validation_alert'
              style={{ justifyContent: 'space-between' }}
            >
              <span>{validationAlert.summary}</span>
              <button
                type='button'
                data-cy='dismiss_validation_alert_button'
                onClick={() => setValidationAlert(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1.1em',
                  color: 'inherit',
                }}
              >
                ×
              </button>
            </div>
          )}

          {loading && (
            <p>{I18n.translate('decaf.transports.pending_integrations.loading')}</p>
          )}

          {!loading && pendingIntegrations.length === 0 && (
            <p>{I18n.translate('decaf.transports.pending_integrations.empty')}</p>
          )}

          {!loading && pendingIntegrations.length > 0 && (
            <>
              <div className='input-group'>
                <input
                  type='text'
                  data-cy='pending_netsuite_integrations_filter'
                  placeholder={I18n.translate(
                    'decaf.transports.pending_integrations.filter_placeholder'
                  )}
                  value={transportFilter}
                  onChange={(event) => setTransportFilter(event.target.value)}
                />
              </div>

              <table
                data-cy='pending_netsuite_integrations_table'
                style={{ minWidth: '60rem' }}
              >
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>
                      {I18n.translate('decaf.transports.pending_integrations.transport')}
                    </th>
                    <th style={{ width: '100%', paddingRight: '15px' }}>
                      {I18n.translate(
                        'decaf.transports.pending_integrations.pending_icos'
                      )}
                    </th>
                    <th style={{ whiteSpace: 'nowrap' }}>
                      {I18n.translate('decaf.transports.pending_integrations.status')}
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredIntegrations.map((integration) => {
                    const queuedMessage = resultByTransportId[integration.transport_id];

                    return (
                      <tr key={integration.transport_id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {integration.local_identifier}
                        </td>
                        <td style={{ whiteSpace: 'normal', paddingRight: '15px' }}>
                          {integration.pending_icos.join(', ')}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {queuedMessage ? (
                            <p
                              className='alert-message alert-success'
                              data-cy='netsuite_integration_queued_message'
                            >
                              {queuedMessage}
                            </p>
                          ) : integration.last_error ? (
                            <div
                              className='alert-message alert-error'
                              data-cy='netsuite_integration_error_icon'
                              title={integration.last_error}
                              style={{ fontWeight: 'bold' }}
                            >
                              <svg
                                className='icon'
                                fill={COLORS.RED}
                                width='1.25em'
                                height='1.25em'
                                style={{ marginRight: '0.4em' }}
                              >
                                <use href='#failed' />
                              </svg>
                              {I18n.translate(
                                'decaf.transports.pending_integrations.error_icon_title'
                              )}
                            </div>
                          ) : (
                            <span data-cy='netsuite_integration_ready_status'>
                              {I18n.translate(
                                'decaf.transports.pending_integrations.status_ready'
                              )}
                            </span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            type='button'
                            className='button button--small'
                            data-cy='retry_netsuite_integration_button'
                            onClick={handleRetry(integration.transport_id)}
                          >
                            {I18n.translate(
                              'decaf.transports.pending_integrations.retry'
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </Overlay>
      )}
    </>
  );
};

export default PendingIntegrationsDialog;
